import {urlAsUrlInstance} from "../helpers/url";

/**
 * This class is used to emit signals to the native app.
 * It does this by constructing a URL with the signal data and then triggering a click event on a hidden link element.
 */
export class NativeAppSignalEmitter {

    constructor(props) {
        this.isNativeApp = getProperty(props, "is_native_app");
        this.nativeAppPlatform = getProperty(props, "native_app_platform");
        this.nativeAppVersion = getProperty(props, "native_app_version");
        this.nativeAppUrlScheme = getProperty(props,"native_app_url_scheme", true);
        this.nativeAppSignalHost = getProperty(props, "native_app_signal_host", true);
        this.nativeAppSignalEndpoint = getProperty(props,"native_app_signal_endpoint", true);

        this.endpointUrl = this.constructEndpointUrl();

        this.rootElement = document.bodyElement || document.body;
        this.element = this.buildLinkElement();
    }

    // Build (or reuse) the hidden anchor used to emit signals to the native app. A single shared anchor is reused
    // across every emitter instance: the anchor holds no per-instance state (its `href` is set just-in-time before
    // each click), so creating one per instance only leaks detached DOM nodes. Reusing the first anchor found keeps
    // the count at one regardless of how many components construct an emitter.
    buildLinkElement() {
        let element = this.rootElement.querySelector(".onlineAppNav__nativeAppSignalLink");
        if (element) return element;
        element = document.createElement("a");
        element.setAttribute("class", `onlineAppNav__nativeAppSignalLink`);
        element.setAttribute(
            "style",
            `position: absolute; top: 100%; opacity: 0;`,
        );
        element.setAttribute("href", "javascript:void(0);");
        element.innerHTML = "";
        this.rootElement.insertBefore(element, null);
        return element;
    }

    emit(signalName, options={}) {
        if (!this.endpointUrl) {
          console.warn("[NativeAppSignalEmitter] Missing endpointUrl!");
          return;
        }
        const url = this.getUrlForData(signalName, options);
        // The click event is triggered within a setTimeout callback.
        // This fixes an issue where this new 'click' event would prevent any default action from happening within the Android native app webview.
        window.setTimeout(() => {
            this.element.setAttribute("href", url);
            this.element.click();
            console.log("[NativeAppSignalEmitter] emit() url: " + this.element.getAttribute("href"));
        }, 10);
    }

    getUrlForData(signalName, options={}) {
        const endpointUrl = new URL(this.endpointUrl); // Duplicate the URL instance for modification.
        endpointUrl.searchParams.append("signalName", signalName);
        for (let key in options) {
            endpointUrl.searchParams.append(key, options[key]);
        }
        return endpointUrl.toString();
    }

    constructEndpointUrl() {
        if (!this.nativeAppUrlScheme) return null;
        if (!this.nativeAppSignalHost) return null;
        if (!this.nativeAppSignalEndpoint) return null;
        return urlAsUrlInstance(`${this.nativeAppUrlScheme}://${this.nativeAppSignalHost}${this.nativeAppSignalEndpoint}`);
    }
}

function getProperty(props, propName, isRequired=false) {
    const property = props.hasOwnProperty(propName) ? props[propName] : null;
    if (isRequired && property == null) {
        console.warn(`[NativeAppSignalEmitter] Missing required property: ${propName}`);
    }
    return property;
}